/**
 * Provisioning Browser-Step Manifest
 *
 * The canonical, shared inventory of every portal step the provisioning
 * runner can execute against a vendor portal: Playwright browser steps, plus
 * the App Group pair that runs over fastlane Spaceship (`sessionKind`). Consumed by three
 * surfaces that must never drift apart:
 *
 *   - the runner (`apps/browser-runner`), whose executable catalog pins
 *     itself to this manifest in a contract test — ids, deps, and lanes here
 *     ARE the catalog
 *   - the backend browser-dispatch service, which resolves "which steps are
 *     ready" and groups them into parallel dispatches by session slug
 *   - the web dashboard, which renders trigger buttons and status per step
 *
 * Source of the step set: apps/browser-runner/src/registry/catalog.ts plus
 * the gap audit against the "App launch setup template" runbook (Notion,
 * 2026-08-19). Steps marked `implemented: false` are contract-first: declared
 * here so backend/web can route them, failing safe in the runner until its
 * catalog entry lands.
 *
 * Membership rule (2026-08-19): a step belongs here ONLY when no official API
 * can do the work. Anything the vendor exposes over an API the backend
 * orchestrator does directly (e.g. bundle ids + capabilities via the ASC API
 * in `create-apple-identifiers`); Playwright is the fallback for the
 * portal-only remainder, never the default.
 */

import { z } from 'zod'

/**
 * Parallelism unit: dispatches sharing a session slug run serially; distinct slugs may run concurrently.
 *
 * `apple-spaceship` is the fastlane Spaceship cookie session the browserless App Group steps run on
 * (SCH-1795).
 */
export const BROWSER_SESSION_SLUGS = ['apple-developer', 'apple-spaceship', 'google-portal'] as const
export const BrowserSessionSlugSchema = z.enum(BROWSER_SESSION_SLUGS)
export type BrowserSessionSlug = z.infer<typeof BrowserSessionSlugSchema>

export const BROWSER_STEP_LANES = ['apple', 'asc', 'play', 'firebase'] as const
export const BrowserStepLaneSchema = z.enum(BROWSER_STEP_LANES)
export type BrowserStepLane = z.infer<typeof BrowserStepLaneSchema>

export interface BrowserStepManifestEntry {
  /** Catalog step id — the public address `journey run <id>` resolves. */
  id: string
  title: string
  lane: BrowserStepLane
  /** Which stored browser session the step needs (drives parallel grouping). */
  sessionSlug: BrowserSessionSlug
  /**
   * How the runner drives the step. `browser` loads a portal page in Chromium; `spaceship` calls
   * Apple's portal through fastlane Spaceship, with no page, no Save and no screenshot evidence.
   */
  sessionKind: 'browser' | 'spaceship'
  /** Ids of manifest steps that must have succeeded first. */
  deps: string[]
  /** Backend orchestrator step whose manual gate this step closes, if any. */
  backendStepName?: string
  /** How the gate closes: adopt = backend retry re-discovers; report = manual-complete with output. */
  closure?: 'adopt' | 'report'
  /** False while the CLI catalog entry is still to be written (contract-first). */
  implemented: boolean
  /**
   * True for a step only its own launcher dispatches, never a provisioning run's default sweep: a
   * read the dashboard asks for on demand, such as the Resolution Center fetch (SCH-2889 U3).
   */
  onDemand?: boolean
}

const step = (
  id: string,
  title: string,
  lane: BrowserStepLane,
  deps: string[],
  extra?: Partial<BrowserStepManifestEntry>
): BrowserStepManifestEntry => ({
  id,
  title,
  lane,
  sessionSlug: lane === 'apple' || lane === 'asc' ? 'apple-developer' : 'google-portal',
  sessionKind: 'browser',
  deps,
  implemented: true,
  ...extra
})

const SPACESHIP = { sessionSlug: 'apple-spaceship', sessionKind: 'spaceship' } as const

/**
 * Dependency-ordered, mirroring the CLI catalog order (apple → asc → play →
 * firebase): every step comes after all of its deps. Order within the manifest
 * is the execution order for steps bundled into one dispatch, and backend
 * readiness and setup-graph batching rely on it —
 * tests/provisioning-browser-steps.test.ts pins the invariant.
 */
export const BROWSER_STEP_MANIFEST: BrowserStepManifestEntry[] = [
  // --- Apple Developer portal ---
  // Bundle-id registration and capability enablement are NOT browser steps:
  // POST /v1/bundleIds and /v1/bundleIdCapabilities work with an ADMIN ASC API
  // key (verified live 2026-08-18), so the backend orchestrator's
  // `create-apple-identifiers` step owns them. Only the surfaces Apple's API
  // 404s on stay here: App Group create/assign and the APNs auth key. The
  // assign/key steps therefore assume the bundle ids already exist (the
  // orchestrator creates them before any browser dispatch is worth running)
  // and fail loudly in the runner when they do not.
  // The App Group pair runs over fastlane Spaceship on the `apple-spaceship` cookie session, not
  // in a browser (SCH-1795). Assignment covers the app, the OneSignal NSE and the share extension,
  // and registers the share-extension identifier when a school has not built v1.9 yet.
  step('apple.app-group', 'App Group', 'apple', [], SPACESHIP),
  step('apple.app-group-assign', 'App Group assigned to all three App IDs', 'apple', ['apple.app-group'], SPACESHIP),
  // The APNs key and its vault check run over Spaceship too (SCH-2236): the key list, the create
  // and the one-time .p8 download are portal calls on the same cookie session.
  step('apple.apns-key', 'APNs key (topic-scoped, production)', 'apple', [], SPACESHIP),
  step('apple.apns-vault', 'APNs key deposited in the vault', 'apple', ['apple.apns-key'], SPACESHIP),

  // --- App Store Connect ---
  // The app record and the App Privacy declaration run over fastlane Spaceship too, through the
  // web session's `iris` API: Apple's public API refuses both (POST /v1/apps is 403; App Privacy
  // has no public resource), and the cookie session reaches them with no browser.
  step('asc.app-record', 'App Store Connect app record', 'asc', [], {
    backendStepName: 'create-asc-app',
    closure: 'adopt',
    ...SPACESHIP
  }),
  step('asc.app-info', 'App Information — name + subtitle', 'asc', ['asc.app-record']),
  step('asc.subtitle', 'App Information — subtitle', 'asc', ['asc.app-record']),
  step('asc.category', 'App Information — categories', 'asc', ['asc.app-record']),
  step('asc.content-rights', 'App Information — content rights', 'asc', ['asc.app-record']),
  step('asc.age-rating', 'App Information — age rating', 'asc', ['asc.app-record']),
  step('asc.privacy-policy', 'App Privacy — privacy policy URL', 'asc', ['asc.app-record']),
  step('asc.privacy-data-types', 'App Privacy — data collection questionnaire', 'asc', ['asc.app-record'], SPACESHIP),
  step('asc.pricing', 'Pricing and Availability — free tier', 'asc', ['asc.app-record']),
  step('asc.listing-text', 'Version — listing text', 'asc', ['asc.app-record']),
  step('asc.screenshots', 'Version — screenshots', 'asc', ['asc.app-record']),
  step('asc.review-info', 'Version — App Review information', 'asc', ['asc.app-record']),
  step('asc.release-option', 'Version — release option', 'asc', ['asc.app-record']),
  // Resets the summary rating when the version is released, over Spaceship (Apple's public API has
  // no such field). Only an upgrade that asks for it dispatches this, on the App Store version it
  // made (`--expected-version`); a provisioning run's default sweep must never reset a live rating.
  step('asc.reset-ratings', 'Reset the App Store summary rating', 'asc', ['asc.app-record'], {
    ...SPACESHIP,
    onDemand: true
  }),
  // The reviewer's messages in the Resolution Center, read for the Rejections tab (SCH-2889 U3).
  // On demand only, and no dep: the app record exists by the time Apple can reject anything, and
  // an `asc.app-record` probe in front of it would only spend another Apple call.
  step('asc.review-messages', 'App Review — Resolution Center messages', 'asc', [], {
    ...SPACESHIP,
    onDemand: true
  }),

  // --- Google Play Console ---
  step('play.shell', 'Play app shell', 'play', [], {
    backendStepName: 'create-google-play-app',
    closure: 'report'
  }),
  // The App content steps sit in the runner catalog's order (apps/browser-runner/src/steps/play),
  // which is dependency order: each step comes after every step it depends on.
  step('play.advertising-id', 'Play advertising ID declaration', 'play', ['play.shell']),
  step('play.privacy-policy', 'Play privacy policy URL', 'play', ['play.shell']),
  step('play.content-rating', 'Play content rating', 'play', ['play.shell']),
  step('play.health', 'Play health declaration', 'play', ['play.shell']),
  step('play.ads', 'Play ads declaration', 'play', ['play.shell']),
  step('play.app-access', 'Play app access (reviewer demo credentials)', 'play', ['play.shell']),
  // Play itself enforces this order: Target audience renders no form until Ads and Sign in details
  // are on record (recon 2026-09-17 — the page shows a prerequisites surface instead).
  step('play.target-audience', 'Play target audience', 'play', ['play.shell', 'play.ads', 'play.app-access']),
  step('play.government-apps', 'Play government apps declaration', 'play', ['play.shell']),
  step('play.financial-features', 'Play financial features declaration', 'play', ['play.shell']),
  // Play keeps Data safety's Save disabled until a privacy policy URL is on record (2026-09-26), and
  // a policy saved moments before can still read as missing, so it runs late, well after the policy.
  step('play.data-safety', 'Play data safety', 'play', ['play.shell', 'play.privacy-policy']),
  // The App content overview read-back, after every section it reads.
  step('play.declarations', 'Play app-content declarations', 'play', ['play.shell']),
  step('play.store-settings', 'Play store settings — category + contact details', 'play', ['play.shell']),
  step('play.countries', 'Play countries and regions availability', 'play', ['play.shell']),
  step('play.listing-text', 'Play store listing text', 'play', ['play.shell']),
  step('play.graphics', 'Play store graphics', 'play', ['play.shell']),

  // The three declarations Play raises from the UPLOADED BUNDLE's merged manifest, rather than
  // from the App content wizard the nine above answer. They appeared for the first time in
  // September 2026 and will meet every school app on its next release.
  step('play.foreground-service', 'Play foreground service permissions declaration', 'play', ['play.shell']),
  step('play.full-screen-intent', 'Play full-screen intent declaration', 'play', ['play.shell']),
  step('play.photo-video', 'Play photo and video permissions declaration', 'play', ['play.shell']),

  // Publishes the first production release, which is what takes the app out of "draft app" state.
  // The Publishing API cannot do it: while an app is a draft it accepts only `status: draft`.
  // The three declarations are deps: Play raises them from the bundle on this release and blocks
  // Save until they are answered, and their probes read this release's own review page.
  step('play.first-release', 'Play first release — create the production release and send it for review', 'play', [
    'play.shell',
    'play.countries',
    'play.foreground-service',
    'play.full-screen-intent',
    'play.photo-video'
  ]),
  // Keeps managed publishing on (SCH-2922): no Publisher API toggles it. Only `play.shell` as a dep,
  // since a dep is run, and this must never submit a release. Before the app is live Google offers
  // no toggle, and the step reports `blocked` with `STEP_NOT_YET_AVAILABLE_FIELD` (api/mobile-apps) set.
  step('play.managed-publishing', 'Play managed publishing — keep it turned on', 'play', ['play.shell']),

  // Managed publishing holds every approved change in Publishing overview until someone clicks
  // "Publish N change(s)", and the Publishing API has no call for that click. Refuses unless the
  // ready-to-publish list names the version the dispatch expects (`--expected-version`).
  step('play.publish-changes', 'Play publish — publish the approved changes managed publishing is holding', 'play', [
    'play.shell'
  ])
  // No Firebase console steps remain. The project, its apps and configs, and its FCM key are all
  // created over the API by the backend (`ensure-firebase-project`, `create-firebase-apps`,
  // `create-fcm-sender`; SCH-2459).
]

export const BROWSER_STEP_IDS = BROWSER_STEP_MANIFEST.map(entry => entry.id)

export function browserStepById(id: string): BrowserStepManifestEntry | undefined {
  return BROWSER_STEP_MANIFEST.find(entry => entry.id === id)
}
